import { addDays, format, parseISO } from "date-fns";
import { dailyCountsFromGroupedRows, dayLabelWithWeekday, monthLabelFromIndex } from "../../../dashboard-framework/aggregateRows";
import { DateRange } from "../../../dashboard-framework/types";
import { NFB_SERVICE, PSY_SERVICE } from "../../../utils/constants";
import { TREATMENT_SERVICE_MIX_LABELS } from "../shared/sharedMetrics";
import {
    BAD_LEAD_CATEGORY,
    BOOKING_CANCELLED_STATUSES,
    BOOKING_DONE_STATUSES,
    EVALUATION_DONE_PROGRESS,
    LEAD_QUALIFIED_CATEGORY_BY_STATUS,
    OPEN_TREATMENT_STATUSES,
} from "./digitalMarketingConfig";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Índice da semana = nº do dia (desde epoch) da segunda-feira dessa semana.
const weekIndexFromIso = (isoTimestamp: string) => {
    const date = new Date(`${isoTimestamp.slice(0, 10)}T00:00:00Z`);
    const mondayOffset = (date.getUTCDay() + 6) % 7;
    const weekStartMs = date.getTime() - mondayOffset * MS_PER_DAY;
    const weekIndex = Math.round(weekStartMs / MS_PER_DAY);
    return weekIndex;
};

export const weekLabelFromIndex = (weekIndex: number) => {
    const weekStart = new Date(weekIndex * MS_PER_DAY);
    const weekEnd = new Date(weekIndex * MS_PER_DAY + 6 * MS_PER_DAY);
    const formatDay = (date: Date) => `${String(date.getUTCDate()).padStart(2, "0")}/${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
    const label = `${formatDay(weekStart)}–${formatDay(weekEnd)}`;
    return label;
};

export const qualifiedCategoryForStatus = (status: string | null | undefined) => {
    if (!status) return "Unknown";
    const category = LEAD_QUALIFIED_CATEGORY_BY_STATUS[status] || "Unknown";
    return category;
};

// Lead qualificada = tudo o que não é Bad lead, incluindo as ainda sem triagem (Pending contact e Unknown).
// Denominador de todos os "custo por lead" do dashboard — ver BAD_LEAD_STATUSES.
export const isQualifiedLeadStatus = (status: string | null | undefined) => {
    const isQualified = qualifiedCategoryForStatus(status) !== BAD_LEAD_CATEGORY;
    return isQualified;
};

// Réplica da fórmula Zoho lead_type: if(age>17,'Adult', if(age<18,'Child','unknown')) com age = anos
// completos. Sem data de nascimento → "Desconhecido" (o "unknown" do Zoho, localizado). Idade em anos
// completos (floor) para bater com o age_years inteiro do Zoho (um jovem de 17,5 anos = 17 = Criança).
export const leadTypeFromBirthday = (birthday: string | null | undefined, now: Date) => {
    if (!birthday) return "Desconhecido";
    const birthDate = new Date(`${birthday.slice(0, 10)}T00:00:00Z`);
    const ageMs = now.getTime() - birthDate.getTime();
    const completedYears = Math.floor(ageMs / (365.25 * MS_PER_DAY));
    const leadType = completedYears > 17 ? "Adulto" : "Criança";
    return leadType;
};

export const LEAD_TYPE_SERIES_KEYS = ["Adulto", "Criança", "Desconhecido"];

// Lead/session client type and the chips derived from it: they live in shared/ since reception-kpis
// started showing the same gauges. Re-exported here for the existing importers.
export { leadTypeChips, leadTypeCounts, leadTypeFromClientType, sessionClientType } from "../shared/leadClientTypes";
export type { SessionLeadType } from "../shared/leadClientTypes";

type WeeklyBucketItem = {
    dateIso: string;
    seriesKey: string;
};

export const weeklySeriesPoints = (items: Array<WeeklyBucketItem>, seriesKeys: Array<string>) => {
    if (items.length === 0) return [] as Array<Record<string, string | number>>;

    const weekIndexes = items.map((item) => weekIndexFromIso(item.dateIso));
    const firstWeekIndex = Math.min(...weekIndexes);
    const lastWeekIndex = Math.max(...weekIndexes);

    const points = [];
    for (let weekIndex = firstWeekIndex; weekIndex <= lastWeekIndex; weekIndex += 7) {
        const point: Record<string, string | number> = { monthLabel: weekLabelFromIndex(weekIndex) };
        let weekTotal = 0;
        seriesKeys.forEach((seriesKey) => {
            const seriesCount = items.filter((item) => weekIndexFromIso(item.dateIso) === weekIndex && item.seriesKey === seriesKey).length;
            point[seriesKey] = seriesCount;
            weekTotal += seriesCount;
        });
        // `total` = soma das séries PEDIDAS, para a linha de Total bater sempre com as linhas do gráfico
        // (itens com uma seriesKey fora da lista ficam de fora, como já ficam das séries). Só aparece nos
        // gráficos que incluam a série "total" — nos outros é uma chave extra ignorada.
        point.total = weekTotal;
        points.push(point);
    }
    return points;
};

type CohortLeadCountsByMonth = Map<number, number>;

type CohortTreatmentItem = {
    date_of_start: string | null;
    client: { lead: { date_created: string } | null } | null;
};

export const cohortKpis = (
    leadCountsByMonthIndex: CohortLeadCountsByMonth,
    treatmentItems: Array<CohortTreatmentItem>,
    firstMonthIndex: number,
    lastMonthIndex: number
) => {
    let leads = 0;
    leadCountsByMonthIndex.forEach((count, monthIndex) => {
        if (monthIndex >= firstMonthIndex && monthIndex <= lastMonthIndex) leads += count;
    });

    const cohortTreatments = treatmentItems.filter((item) => {
        const leadCreated = item.client?.lead?.date_created;
        if (!leadCreated) return false;
        const monthIndex = Number(leadCreated.slice(0, 4)) * 12 + (Number(leadCreated.slice(5, 7)) - 1);
        return monthIndex >= firstMonthIndex && monthIndex <= lastMonthIndex;
    });
    const evaluations = cohortTreatments.length;
    const clients = cohortTreatments.filter((item) => item.date_of_start !== null).length;

    const monthsInWindow = lastMonthIndex - firstMonthIndex + 1;
    const pct = (numerator: number, denominator: number) =>
        denominator > 0 ? Math.round((numerator / denominator) * 1000) / 10 : null;

    const kpis = {
        averageMonthlyEvaluations: monthsInWindow > 0 ? Math.round(evaluations / monthsInWindow) : null,
        averageMonthlySales: monthsInWindow > 0 ? Math.round(clients / monthsInWindow) : null,
        leadsToEvaluationsPct: pct(evaluations, leads),
        leadsToClientsPct: pct(clients, leads),
        evaluationsToClientsPct: pct(clients, evaluations),
        leads,
        evaluations,
        clients,
    };
    return kpis;
};

export const averageOfCounts = (countsByMonthIndex: Map<number, number>, firstMonthIndex: number, lastMonthIndex: number) => {
    let total = 0;
    const monthsInWindow = lastMonthIndex - firstMonthIndex + 1;
    for (let monthIndex = firstMonthIndex; monthIndex <= lastMonthIndex; monthIndex++) {
        total += countsByMonthIndex.get(monthIndex) || 0;
    }
    const average = monthsInWindow > 0 ? Math.round(total / monthsInWindow) : null;
    return average;
};

// ────────────────────────────────────────────────────────────────────────────────────────────
// CUSTOS PPC — helpers
// ────────────────────────────────────────────────────────────────────────────────────────────

type GroupedSumRow = Record<string, any>;

// Soma mensal a partir de rows de agregado com groupBy year(field),month(field) e aggregate { sum: field }.
export const monthlySumFromGroupedRows = (rows: Array<GroupedSumRow>, dateField: string, sumField: string) => {
    const sumsByMonthIndex = new Map<number, number>();
    rows.forEach((row) => {
        const year = Number(row[`${dateField}_year`]);
        const month = Number(row[`${dateField}_month`]);
        if (!year || !month) return;
        const monthIndex = year * 12 + (month - 1);
        const value = Number(row.sum?.[sumField] || 0);
        sumsByMonthIndex.set(monthIndex, (sumsByMonthIndex.get(monthIndex) || 0) + value);
    });
    return sumsByMonthIndex;
};

// Soma diária a partir de rows de agregado com groupBy year,month,day e aggregate { sum: field }.
export const dailySumFromGroupedRows = (rows: Array<GroupedSumRow>, dateField: string, sumField: string) => {
    const sumsByDayKey = new Map<string, number>();
    rows.forEach((row) => {
        const year = Number(row[`${dateField}_year`]);
        const month = Number(row[`${dateField}_month`]);
        const day = Number(row[`${dateField}_day`]);
        if (!year || !month || !day) return;
        const dayKey = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        sumsByDayKey.set(dayKey, (sumsByDayKey.get(dayKey) || 0) + Number(row.sum?.[sumField] || 0));
    });
    return sumsByDayKey;
};

type ClinicDayCountRow = { clinic: number | string | null; count: string | number } & Record<string, string | number | null | undefined>;

// Pontos diários de leads criadas ao longo de um intervalo: uma série por clínica pedida (id → key) mais
// "total" (todas as leads do dia, incluindo as sem clínica). Rows: agregado groupBy year,month,day,clinic
// de date_created. Cada dia do intervalo produz um ponto (dias sem leads ficam a 0).
export const dailyLeadsByClinicPoints = (rows: Array<ClinicDayCountRow>, range: DateRange, clinics: Array<{ id: number; key: string }>) => {
    const totalByDayKey = dailyCountsFromGroupedRows(rows, "date_created");
    const countsByClinicKey = new Map<string, Map<string, number>>();
    clinics.forEach((clinic) => {
        const clinicRows = rows.filter((row) => Number(row.clinic) === clinic.id);
        countsByClinicKey.set(clinic.key, dailyCountsFromGroupedRows(clinicRows, "date_created"));
    });
    const points = [];
    const lastDay = parseISO(range.lt);
    for (let day = parseISO(range.gte); day < lastDay; day = addDays(day, 1)) {
        const dayKey = format(day, "yyyy-MM-dd");
        const point: Record<string, string | number> = { monthLabel: dayLabelWithWeekday(day), total: totalByDayKey.get(dayKey) || 0 };
        clinics.forEach((clinic) => {
            point[clinic.key] = countsByClinicKey.get(clinic.key)?.get(dayKey) || 0;
        });
        points.push(point);
    }
    return points;
};

// Fonte da lead (nível de topo da tabela de grupos de anúncios): só facebook/google são pagos; o resto
// (orgânico, direto, referências, null) cai em "Direct / no tracking" — réplica dos 3 grupos do Zoho.
export const sourceLabelOf = (utmSource: string | null | undefined) => {
    if (utmSource === "facebook") return "Facebook";
    if (utmSource === "google") return "Google";
    return "Direct / no tracking";
};

// Fonte do gasto (marketing_spend.platform é sempre facebook/google).
export const platformSourceLabel = (platform: string | null | undefined) => {
    if (platform === "facebook") return "Facebook";
    if (platform === "google") return "Google";
    return "Direct / no tracking";
};

export const AD_GROUP_CAMPAIGN_FALLBACK = "(sem campanha)";

type AdGroupLead = { utm_source: string | null; utm_campaign: string | null; utm_term: string | null; progress: string | null; status: string | null };
type AdGroupSpend = { amount_spent: string | number | null; platform: string | null; ad: { utm_campaign: string | null } | null; adset: { name: string | null } | null };
export type AdGroupRow = { source: string; group: string; leads: number; qualifiedLeads: number; booked: number; done: number; cost: number };

// Facebook: grupo de anúncios (utm_term = id do adset no Facebook). Google e sem tracking: campanha — as leads
// do Google trazem a keyword no utm_term, não o grupo de anúncios, por isso não há como as ligar ao grupo.
export const adGroupLabelOfLead = (
    lead: { utm_source: string | null; utm_campaign: string | null; utm_term: string | null },
    facebookAdsetNameById: Map<string, string>
) => {
    if (lead.utm_source === "facebook") return adsetLabelOf(lead.utm_term, facebookAdsetNameById);
    const label = lead.utm_campaign || AD_GROUP_CAMPAIGN_FALLBACK;
    return label;
};

const adGroupLabelOfSpend = (spendRow: AdGroupSpend) => {
    if (spendRow.platform === "facebook") return spendRow.adset?.name || SEM_ADSET_LABEL;
    const label = spendRow.ad?.utm_campaign || AD_GROUP_CAMPAIGN_FALLBACK;
    return label;
};

// Constrói uma linha por (fonte, grupo) juntando leads (contagem total + qualificadas + marcações via progress)
// e custo (marketing_spend). Grupo = grupo de anúncios no Facebook, campanha no Google/sem tracking — ver
// adGroupLabelOfLead. Réplica da tabela Zoho "Efficiency of Ad Groups": marcação = lead com progress
// evaluation_booked/evaluation_done; feita = progress evaluation_done. qualifiedLeads é o denominador do
// custo/lead — ver isQualifiedLeadStatus.
export const buildAdGroupRows = (leads: Array<AdGroupLead>, spend: Array<AdGroupSpend>, facebookAdsetNameById: Map<string, string>) => {
    const rowsByKey = new Map<string, AdGroupRow>();
    const keyOf = (source: string, group: string) => `${source}|||${group}`;
    const ensureRow = (source: string, group: string) => {
        const key = keyOf(source, group);
        let row = rowsByKey.get(key);
        if (!row) {
            row = { source, group, leads: 0, qualifiedLeads: 0, booked: 0, done: 0, cost: 0 };
            rowsByKey.set(key, row);
        }
        return row;
    };

    leads.forEach((lead) => {
        const source = sourceLabelOf(lead.utm_source);
        const row = ensureRow(source, adGroupLabelOfLead(lead, facebookAdsetNameById));
        row.leads += 1;
        if (isQualifiedLeadStatus(lead.status)) row.qualifiedLeads += 1;
        if (lead.progress === "evaluation_booked" || lead.progress === "evaluation_done") row.booked += 1;
        if (lead.progress === "evaluation_done") row.done += 1;
    });

    spend.forEach((spendRow) => {
        const source = platformSourceLabel(spendRow.platform);
        const row = ensureRow(source, adGroupLabelOfSpend(spendRow));
        row.cost += Number(spendRow.amount_spent || 0);
    });

    const rows = [...rowsByKey.values()];
    return rows;
};

export type AdsetStatusSession = { id: number | string; status: string | null; date_created: string | null; start_date: string | null };
// utm_term = id do grupo de anúncios (adset) do Facebook. first_name/last_name/date_created/status/utm_campaign
// só alimentam as colunas do drill.
export type AdsetStatusLead = {
    id: number | string;
    utm_term: string | null;
    utm_campaign: string | null;
    sessions: Array<AdsetStatusSession> | null;
    first_name?: string | null;
    last_name?: string | null;
    date_created?: string | null;
    status?: string | null;
};
export type LeadBookingState = "done" | "pending" | "cancelled" | "notBooked";
// qualified = leads potenciais (não Bad lead), denominador do custo/lead potencial — não é um segmento do empilhamento.
export type AdsetBookingStatePoint = { adset: string; qualified: number } & Record<LeadBookingState, number>;

// Última marcação = a sessão CRIADA mais recentemente (empate → id maior). Pela criação e não pela data da
// sessão: uma remarcação é sempre criada depois da marcação que substitui, mesmo quando antecipa a visita.
export const latestCreatedSession = (sessions: Array<AdsetStatusSession> | null) => {
    const sortedSessions = [...(sessions || [])].sort((first, second) => {
        const firstCreated = first.date_created || "";
        const secondCreated = second.date_created || "";
        if (firstCreated !== secondCreated) return secondCreated < firstCreated ? -1 : 1;
        return Number(second.id) - Number(first.id);
    });
    const latestSession = sortedSessions[0] ?? null;
    return latestSession;
};

export const leadBookingStateOf = (sessions: Array<AdsetStatusSession> | null) => {
    const latestSession = latestCreatedSession(sessions);
    if (!latestSession) return "notBooked";
    const status = latestSession.status || "";
    if (BOOKING_DONE_STATUSES.includes(status)) return "done";
    if (BOOKING_CANCELLED_STATUSES.includes(status)) return "cancelled";
    return "pending";
};

// Nome do grupo de anúncios a partir do utm_term da lead (id do adset no Facebook → marketing_adset.name).
// Um id que não exista no CRM mantém-se visível pelo próprio id, em vez de se perder num balde genérico.
export const adsetLabelOf = (utmTerm: string | null, adsetNameById: Map<string, string>) => {
    if (!utmTerm) return SEM_ADSET_LABEL;
    const label = adsetNameById.get(utmTerm) || `Grupo de anúncios ${utmTerm}`;
    return label;
};

export const totalLeadsOfAdsetPoint = (point: AdsetBookingStatePoint) => point.done + point.pending + point.cancelled + point.notBooked;

// Grupo de anúncios de uma lead para as colunas dos drills: só no Facebook (no Google o utm_term é a palavra-chave)
// e só quando a lead traz o utm_term — sem ele a célula fica vazia em vez de um rótulo genérico.
export const facebookAdsetNameOfLead = (lead: { utm_source: string | null; utm_term: string | null }, adsetNameById: Map<string, string>) => {
    if (lead.utm_source !== "facebook" || !lead.utm_term) return null;
    const adsetName = adsetLabelOf(lead.utm_term, adsetNameById);
    return adsetName;
};

// Um ponto por grupo de anúncios com as leads repartidas pelo estado da sua última marcação, ordenado por
// total de leads (desc) e, em empate, pelo nome. Agrupa pelo NOME: cópias do mesmo grupo de anúncios em
// campanhas diferentes partilham o nome e leem-se como um só.
export const adsetBookingStatePoints = (leads: Array<AdsetStatusLead>, adsetNameById: Map<string, string>) => {
    const pointsByAdset = new Map<string, AdsetBookingStatePoint>();
    leads.forEach((lead) => {
        const adset = adsetLabelOf(lead.utm_term, adsetNameById);
        const point = pointsByAdset.get(adset) || { adset, done: 0, pending: 0, cancelled: 0, notBooked: 0, qualified: 0 };
        point[leadBookingStateOf(lead.sessions)] += 1;
        if (isQualifiedLeadStatus(lead.status)) point.qualified += 1;
        pointsByAdset.set(adset, point);
    });
    const points = [...pointsByAdset.values()].sort((first, second) => {
        const totalDifference = totalLeadsOfAdsetPoint(second) - totalLeadsOfAdsetPoint(first);
        if (totalDifference !== 0) return totalDifference;
        return first.adset.localeCompare(second.adset);
    });
    return points;
};

export const pointsWithMoreLeadsThan = (points: Array<AdsetBookingStatePoint>, minimumLeads: number) => {
    const visiblePoints = points.filter((point) => totalLeadsOfAdsetPoint(point) > minimumLeads);
    return visiblePoints;
};

type FacebookSpendByAdsetRow = { adset: number | string | null; sum: { amount_spent: string | number | null } };

// Gasto do período por NOME de grupo de anúncios (o mesmo agrupamento das leads), a partir do agregado
// marketing_spend agrupado pelo registo marketing_adset. Gasto sem grupo de anúncios conhecido fica de fora.
export const facebookSpendByAdsetName = (spendRows: Array<FacebookSpendByAdsetRow>, adsetNameByRecordId: Map<string, string>) => {
    const spendByAdsetName = new Map<string, number>();
    spendRows.forEach((spendRow) => {
        const adsetName = adsetNameByRecordId.get(String(spendRow.adset));
        if (!adsetName) return;
        spendByAdsetName.set(adsetName, (spendByAdsetName.get(adsetName) || 0) + Number(spendRow.sum.amount_spent || 0));
    });
    return spendByAdsetName;
};

const costPerUnitOrNull = (cost: number, units: number) => (cost > 0 && units > 0 ? cost / units : null);

// Custos por grupo de anúncios, na MESMA ordem dos pontos de estado (para os dois gráficos se lerem lado a lado):
// gasto ÷ leads potenciais, ÷ leads com marcação (qualquer estado) e ÷ leads com a última marcação realizada.
export const adsetCostPoints = (statePoints: Array<AdsetBookingStatePoint>, spendByAdsetName: Map<string, number>) => {
    const points = statePoints.map((point) => {
        const spend = spendByAdsetName.get(point.adset) || 0;
        const costPoint = {
            adset: point.adset,
            spend,
            costPerQualifiedLead: costPerUnitOrNull(spend, point.qualified),
            costPerBooking: costPerUnitOrNull(spend, point.done + point.pending + point.cancelled),
            costPerDone: costPerUnitOrNull(spend, point.done),
        };
        return costPoint;
    });
    return points;
};

const SEM_ADSET_LABEL = "Sem grupo de anúncios";

type SpendAdsetItem = { amount_spent: string | number | null; platform: string | null; adset: { id: number | string | null; name: string | null } | null };
export type AdsetSpendRow = { platform: string; adsetName: string; amount_spent: number };

// Agrega o gasto de marketing por GRUPO DE ANÚNCIOS (adset), somando todos os anúncios do mesmo adset.
// Ordena por plataforma (Facebook antes de Google) e, dentro de cada plataforma, por gasto descendente —
// assim a modal do custo mostra os adsets agrupados por plataforma e, em cada grupo, do maior gasto para o menor.
export const spendByAdsetRows = (spend: Array<SpendAdsetItem>) => {
    const rowsByAdsetKey = new Map<string, AdsetSpendRow>();
    spend.forEach((spendRow) => {
        const adsetKey = `${spendRow.platform ?? "none"}|||${spendRow.adset?.id ?? "none"}`;
        let row = rowsByAdsetKey.get(adsetKey);
        if (!row) {
            row = { platform: platformSourceLabel(spendRow.platform), adsetName: spendRow.adset?.name || SEM_ADSET_LABEL, amount_spent: 0 };
            rowsByAdsetKey.set(adsetKey, row);
        }
        row.amount_spent += Number(spendRow.amount_spent || 0);
    });
    const rows = [...rowsByAdsetKey.values()];
    rows.sort((firstRow, secondRow) => {
        if (firstRow.platform !== secondRow.platform) return firstRow.platform < secondRow.platform ? -1 : 1;
        return secondRow.amount_spent - firstRow.amount_spent;
    });
    return rows;
};

// Agrega mapas diários (leads, marcações, custos) em pontos semanais (segunda–domingo, mesma semântica dos
// restantes charts semanais). costPer1stVisit = custo da semana ÷ marcações da semana.
export const weeklyLeadsBookingsCostsPoints = (
    leadsByDay: Map<string, number>,
    bookingsByDay: Map<string, number>,
    costsByDay: Map<string, number>,
    range: DateRange
) => {
    const weekBuckets = new Map<number, { leads: number; bookings: number; costs: number }>();
    for (let day = parseISO(range.gte); day < parseISO(range.lt); day = addDays(day, 1)) {
        const dayKey = format(day, "yyyy-MM-dd");
        const weekIndex = weekIndexFromIso(dayKey);
        const bucket = weekBuckets.get(weekIndex) || { leads: 0, bookings: 0, costs: 0 };
        bucket.leads += leadsByDay.get(dayKey) || 0;
        bucket.bookings += bookingsByDay.get(dayKey) || 0;
        bucket.costs += costsByDay.get(dayKey) || 0;
        weekBuckets.set(weekIndex, bucket);
    }
    const sortedWeeks = [...weekBuckets.keys()].sort((first, second) => first - second);
    const points = sortedWeeks.map((weekIndex) => {
        const bucket = weekBuckets.get(weekIndex) as { leads: number; bookings: number; costs: number };
        const point: Record<string, string | number> = {
            monthLabel: weekLabelFromIndex(weekIndex),
            leads: bucket.leads,
            bookings: bucket.bookings,
            mktCosts: Math.round(bucket.costs * 100) / 100,
            costPer1stVisit: bucket.bookings > 0 ? Math.round((bucket.costs / bucket.bookings) * 100) / 100 : 0,
        };
        return point;
    });
    return points;
};

// ────────────────────────────────────────────────────────────────────────────────────────────
// GRUPO C — helpers (por clínica, eficiência mensal, clínica do tratamento)
// ────────────────────────────────────────────────────────────────────────────────────────────

type GroupedClinicRow = { clinic_id: number | string | null; count: string | number } & Record<string, string | number | null | undefined>;

// Contagens mensais por clínica a partir de rows agrupados por year,month,clinic_id.
export const monthlyCountsByClinic = (rows: Array<GroupedClinicRow>, dateField: string, clinicIds: Array<number>) => {
    const countsByClinic = new Map<number, Map<number, number>>();
    clinicIds.forEach((clinicId) => countsByClinic.set(clinicId, new Map()));
    rows.forEach((row) => {
        const clinicId = Number(row.clinic_id);
        const clinicCounts = countsByClinic.get(clinicId);
        if (!clinicCounts) return;
        const year = Number(row[`${dateField}_year`]);
        const month = Number(row[`${dateField}_month`]);
        if (!year || !month) return;
        const monthIndex = year * 12 + (month - 1);
        clinicCounts.set(monthIndex, (clinicCounts.get(monthIndex) || 0) + Number(row.count));
    });
    return countsByClinic;
};

type TreatmentSession = { service: number | string | null; clinic_id: number | string | null; start_date: string | null };

// Clínica de um tratamento = clínica da 1ª sessão de NFB/Psicologia (treatments não tem clínica direta —
// mesmo padrão da Análise Diária).
export const treatmentClinicId = (sessions: Array<TreatmentSession>) => {
    const firstVisitSessions = sessions
        .filter((session) => [NFB_SERVICE, PSY_SERVICE].includes(Number(session.service)) && session.start_date && session.clinic_id !== null)
        .sort((first, second) => ((first.start_date as string) < (second.start_date as string) ? -1 : 1));
    const clinicId = firstVisitSessions.length ? Number(firstVisitSessions[0].clinic_id) : null;
    return clinicId;
};

type ClinicTreatment = { date_of_start: string | null; sessions: Array<TreatmentSession> | null };

// Vendas mensais por clínica: tratamentos por date_of_start, clínica = 1ª sessão NFB/Psi.
export const salesByClinicMonthly = (treatments: Array<ClinicTreatment>, clinicIds: Array<number>) => {
    const countsByClinic = new Map<number, Map<number, number>>();
    clinicIds.forEach((clinicId) => countsByClinic.set(clinicId, new Map()));
    treatments.forEach((treatment) => {
        if (!treatment.date_of_start) return;
        const clinicId = treatmentClinicId(treatment.sessions || []);
        if (clinicId === null) return;
        const clinicCounts = countsByClinic.get(clinicId);
        if (!clinicCounts) return;
        const monthIndex = Number(treatment.date_of_start.slice(0, 4)) * 12 + (Number(treatment.date_of_start.slice(5, 7)) - 1);
        clinicCounts.set(monthIndex, (clinicCounts.get(monthIndex) || 0) + 1);
    });
    return countsByClinic;
};

type StartedTreatment = { treatmentId: number | string; firstSessionDate: string };

// Tratamentos iniciados (+ por vir) por mês da 1ª sessão NFB/Psi, repartidos pela classificação do
// tratamento (Só NFB / Só Psi / NFB + Psi). Tratamentos sem classificação (sem sessões no universo do
// aggregate de mix) são ignorados.
export const treatmentsStartedByTypePoints = (
    startedTreatments: Array<StartedTreatment>,
    servicesLabelByTreatmentId: Map<number, string>,
    firstMonthIndex: number,
    lastMonthIndex: number
) => {
    const keyByLabel: Record<string, "nfbOnly" | "psyOnly" | "both"> = {
        [TREATMENT_SERVICE_MIX_LABELS.nfbOnly]: "nfbOnly",
        [TREATMENT_SERVICE_MIX_LABELS.psyOnly]: "psyOnly",
        [TREATMENT_SERVICE_MIX_LABELS.both]: "both",
    };
    const countsByMonthIndex = new Map<number, { nfbOnly: number; psyOnly: number; both: number }>();
    startedTreatments.forEach((treatment) => {
        const label = servicesLabelByTreatmentId.get(Number(treatment.treatmentId));
        const typeKey = label ? keyByLabel[label] : undefined;
        if (!typeKey) return;
        const monthIndex = Number(treatment.firstSessionDate.slice(0, 4)) * 12 + (Number(treatment.firstSessionDate.slice(5, 7)) - 1);
        if (monthIndex < firstMonthIndex || monthIndex > lastMonthIndex) return;
        const monthCounts = countsByMonthIndex.get(monthIndex) || { nfbOnly: 0, psyOnly: 0, both: 0 };
        monthCounts[typeKey] += 1;
        countsByMonthIndex.set(monthIndex, monthCounts);
    });
    const points = [];
    for (let monthIndex = firstMonthIndex; monthIndex <= lastMonthIndex; monthIndex++) {
        const monthCounts = countsByMonthIndex.get(monthIndex) || { nfbOnly: 0, psyOnly: 0, both: 0 };
        // `total` = soma dos três tipos, para a linha de Total bater com as linhas do gráfico.
        const point = { monthLabel: monthLabelFromIndex(monthIndex), monthIndex, ...monthCounts, total: monthCounts.nfbOnly + monthCounts.psyOnly + monthCounts.both };
        points.push(point);
    }
    return points;
};

// Pontos mensais do "Mkt Cost Per Goal": gasto total (barra) e custo por lead qualificada/avaliação(feita)/
// booking/tratamento. O custo por lead usa apenas as leads QUALIFICADAS do mês — ver isQualifiedLeadStatus.
export const mktCostPerGoalPoints = (
    spendByMonthIndex: Map<number, number>,
    qualifiedLeadsByMonthIndex: Map<number, number>,
    evaluationsDoneByMonthIndex: Map<number, number>,
    salesByMonthIndex: Map<number, number>,
    bookingsByMonthIndex: Map<number, number>,
    pendingEvaluationsByMonthIndex: Map<number, number>,
    openTreatmentsByMonthIndex: Map<number, number>,
    firstMonthIndex: number,
    lastMonthIndex: number
) => {
    const perUnit = (numerator: number, denominator: number) => (denominator > 0 ? Math.round((numerator / denominator) * 100) / 100 : 0);
    const points = [];
    for (let monthIndex = firstMonthIndex; monthIndex <= lastMonthIndex; monthIndex++) {
        const spend = spendByMonthIndex.get(monthIndex) || 0;
        const qualifiedLeads = qualifiedLeadsByMonthIndex.get(monthIndex) || 0;
        const evaluations = evaluationsDoneByMonthIndex.get(monthIndex) || 0;
        const sales = salesByMonthIndex.get(monthIndex) || 0;
        const bookings = bookingsByMonthIndex.get(monthIndex) || 0;
        const point = {
            monthLabel: monthLabelFromIndex(monthIndex),
            spend: Math.round(spend * 100) / 100,
            costPerLead: perUnit(spend, qualifiedLeads),
            costPerBooking: perUnit(spend, bookings),
            costPerEvaluation: perUnit(spend, evaluations),
            costPerTreatment: perUnit(spend, sales),
            qualifiedLeadsCount: qualifiedLeads,
            bookingsCount: bookings,
            evaluationsCount: evaluations,
            treatmentsCount: sales,
            pendingEvaluationsCount: pendingEvaluationsByMonthIndex.get(monthIndex) || 0,
            openTreatmentsCount: openTreatmentsByMonthIndex.get(monthIndex) || 0,
        };
        points.push(point);
    }
    return points;
};

type EfficiencyTreatment = { date_of_start: string | null; client: { lead: { date_created: string } | null } | null };

// Vendas por mês de CRIAÇÃO da lead (cohort — "Sales" do MKT Results All): tratamentos com date_of_start,
// agrupados pelo mês em que a lead do cliente foi criada. Réplica do join treatments→clients→leads do Zoho.
export const salesByLeadCreationMonth = (treatments: Array<EfficiencyTreatment>) => {
    const salesByMonthIndex = new Map<number, number>();
    treatments.forEach((treatment) => {
        if (!treatment.date_of_start) return;
        const leadCreated = treatment.client?.lead?.date_created;
        if (!leadCreated) return;
        const monthIndex = Number(leadCreated.slice(0, 4)) * 12 + (Number(leadCreated.slice(5, 7)) - 1);
        salesByMonthIndex.set(monthIndex, (salesByMonthIndex.get(monthIndex) || 0) + 1);
    });
    return salesByMonthIndex;
};

const monthIndexOfIsoDate = (isoDate: string) => {
    const monthIndex = Number(isoDate.slice(0, 4)) * 12 + (Number(isoDate.slice(5, 7)) - 1);
    return monthIndex;
};

const incrementMonthCount = (countsByMonthIndex: Map<number, number>, isoDate: string) => {
    const monthIndex = monthIndexOfIsoDate(isoDate);
    countsByMonthIndex.set(monthIndex, (countsByMonthIndex.get(monthIndex) || 0) + 1);
};

type PendingEvaluationLead = { date_created: string; progress: string | null };

// Leads (já filtradas a quem tem 1ª avaliação futura marcada) por mês de criação, sem as que já fizeram avaliação.
export const pendingEvaluationsByLeadCreationMonth = (leads: Array<PendingEvaluationLead>) => {
    const countsByMonthIndex = new Map<number, number>();
    leads
        .filter((lead) => lead.progress !== EVALUATION_DONE_PROGRESS)
        .forEach((lead) => incrementMonthCount(countsByMonthIndex, lead.date_created));
    return countsByMonthIndex;
};

type OpenTreatmentCandidate = EfficiencyTreatment & { treatment_status_new: string | null };

export const openTreatmentsByLeadCreationMonth = (treatments: Array<OpenTreatmentCandidate>) => {
    const countsByMonthIndex = new Map<number, number>();
    treatments.forEach((treatment) => {
        const leadCreated = treatment.client?.lead?.date_created;
        if (!leadCreated || treatment.date_of_start) return;
        if (!treatment.treatment_status_new || !OPEN_TREATMENT_STATUSES.includes(treatment.treatment_status_new)) return;
        incrementMonthCount(countsByMonthIndex, leadCreated);
    });
    return countsByMonthIndex;
};

// Eficiência de marketing por mês de criação da lead: % leads→marcações, leads→avaliações,
// avaliações→clientes, leads→clientes. Marcações vêm de leads.progress; avaliações/clientes do cohort
// de tratamentos ligados à lead (mesma definição dos KPIs de cohort).
export const monthlyEfficiencyPoints = (
    leadCountsByMonthIndex: Map<number, number>,
    bookingCountsByMonthIndex: Map<number, number>,
    treatments: Array<EfficiencyTreatment>,
    firstMonthIndex: number,
    lastMonthIndex: number
) => {
    const evaluationsByMonth = new Map<number, number>();
    const clientsByMonth = new Map<number, number>();
    treatments.forEach((treatment) => {
        const leadCreated = treatment.client?.lead?.date_created;
        if (!leadCreated) return;
        const monthIndex = Number(leadCreated.slice(0, 4)) * 12 + (Number(leadCreated.slice(5, 7)) - 1);
        evaluationsByMonth.set(monthIndex, (evaluationsByMonth.get(monthIndex) || 0) + 1);
        if (treatment.date_of_start !== null && treatment.date_of_start !== undefined) {
            clientsByMonth.set(monthIndex, (clientsByMonth.get(monthIndex) || 0) + 1);
        }
    });

    const pct = (numerator: number, denominator: number) => (denominator > 0 ? Math.round((numerator / denominator) * 1000) / 10 : 0);

    const points = [];
    for (let monthIndex = firstMonthIndex; monthIndex <= lastMonthIndex; monthIndex++) {
        const leads = leadCountsByMonthIndex.get(monthIndex) || 0;
        const bookings = bookingCountsByMonthIndex.get(monthIndex) || 0;
        const evaluations = evaluationsByMonth.get(monthIndex) || 0;
        const clients = clientsByMonth.get(monthIndex) || 0;
        const point = {
            monthLabel: monthLabelFromIndex(monthIndex),
            leadsToBookings: pct(bookings, leads),
            leadsToEvaluations: pct(evaluations, leads),
            evaluationsToClients: pct(clients, evaluations),
            leadsToClients: pct(clients, leads),
        };
        points.push(point);
    }
    return points;
};
